import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * MD3 info card shared by CommunityPage and OrganizationPage:
 * logo (or icon), name, QR code, action buttons and contact details.
 */
export default function CommunityInfoCard({
	logoUri,
	icon,
	name,
	qrcodeUri,
	actions = [],
	address,
	zipCode,
	city,
	phone,
	mail,
	webSiteUrl
}) {
	const contacts = [
		phone && { icon: "phone", href: `tel:${phone}`, label: phone },
		mail && { icon: "email", href: `mailto:${mail}`, label: mail },
		webSiteUrl && {
			icon: "language",
			href: webSiteUrl,
			label: webSiteUrl,
			external: true
		}
	].filter(Boolean);

	return (
		<Card>
			<MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
				<MDBox
					variant="gradient"
					bgColor={logoUri ? "light" : "primary"}
					color="white"
					borderRadius="xl"
					shadow="md"
					display="flex"
					justifyContent="center"
					alignItems="center"
					width="7rem"
					height="7rem"
					mt={-3}
					fontSize="3rem"
				>
					{logoUri ? (
						<MDBox
							component="img"
							src={logoUri}
							alt="Community logo"
							height="100px"
							maxWidth="100%"
							sx={{ objectFit: "contain" }}
						/>
					) : (
						icon
					)}
				</MDBox>
				<MDBox display="flex" alignItems="flex-start" pt={1}>
					{actions.map(action => (
						<Tooltip
							key={action.tooltip}
							title={action.tooltip}
							placement="bottom"
						>
							<span>
								<MDButton
									variant="text"
									color={action.color}
									iconOnly
									href={action.href}
									target={action.href ? "_blank" : undefined}
									onClick={action.onClick}
									disabled={action.disabled}
								>
									<Icon>{action.icon}</Icon>
								</MDButton>
							</span>
						</Tooltip>
					))}
				</MDBox>
			</MDBox>
			<MDBox px={2} pt={2} textAlign="right">
				<MDTypography variant="h5" fontWeight="regular">
					{name}
				</MDTypography>
				{qrcodeUri && (
					<MDBox
						component="img"
						src={qrcodeUri}
						alt="Community qrcode"
						width="200px"
						height="200px"
						mt={2.5}
					/>
				)}
			</MDBox>
			<Divider />
			<MDBox pb={2} px={2}>
				{address && (
					<MDTypography variant="button" color="text" component="p">
						{address}
					</MDTypography>
				)}
				{zipCode && city && (
					<MDTypography variant="button" color="text" component="p">
						{zipCode + " " + city}
					</MDTypography>
				)}
				{contacts.map(({ icon, href, label, external }) => (
					<MDBox key={icon} display="flex" alignItems="center" py={0.5}>
						<MDBox color="text" mr={1} display="flex">
							<Icon color="inherit">{icon}</Icon>
						</MDBox>
						<Link
							href={href}
							{...(external
								? { target: "_blank", rel: "noopener noreferrer" }
								: {})}
						>
							<MDTypography variant="button" color="info">
								{label}
							</MDTypography>
						</Link>
					</MDBox>
				))}
			</MDBox>
		</Card>
	);
}

CommunityInfoCard.propTypes = {
	logoUri: PropTypes.string,
	icon: PropTypes.node,
	name: PropTypes.string,
	qrcodeUri: PropTypes.string,
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			tooltip: PropTypes.string.isRequired,
			icon: PropTypes.string.isRequired,
			color: PropTypes.string,
			href: PropTypes.string,
			onClick: PropTypes.func,
			disabled: PropTypes.bool
		})
	),
	address: PropTypes.string,
	zipCode: PropTypes.string,
	city: PropTypes.string,
	phone: PropTypes.string,
	mail: PropTypes.string,
	webSiteUrl: PropTypes.string
};
